import React from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Users, Award, Clock, MapPin, Phone, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const processSteps = [
    {
      step: 1,
      title: "Visite & diagnostic",
      description: "Inspection complète de votre toiture avec photos et rapport détaillé des problèmes identifiés."
    },
    {
      step: 2,
      title: "Devis personnalisé",
      description: "Proposition claire avec matériaux précis, délais de réalisation et tarifs transparents."
    },
    {
      step: 3,
      title: "Planification chantier",
      description: "Organisation du chantier, commande des matériaux et coordination avec vos contraintes."
    },
    {
      step: 4,
      title: "Réalisation travaux",
      description: "Execution par nos équipes qualifiées avec respect des DTU et normes de sécurité."
    },
    {
      step: 5,
      title: "Contrôle & finitions",
      description: "Vérification finale de la qualité et nettoyage complet du chantier avant réception."
    }
  ];

  const values = [
    {
      icon: CheckCircle,
      title: "Qualité garantie",
      description: "Tous nos travaux respectent les DTU et bénéficient de notre garantie décennale pour votre tranquillité."
    },
    {
      icon: Users,
      title: "Équipe experte",
      description: "Nos couvreurs sont formés aux dernières techniques et utilisent des équipements de sécurité modernes."
    },
    {
      icon: Clock,
      title: "Réactivité",
      description: "Réponse sous 24-48h pour vos devis et intervention rapide, notamment pour les urgences."
    },
    {
      icon: MapPin,
      title: "Proximité",
      description: "Basés à Cugnaux, nous intervenons dans toute l'agglomération toulousaine et l'Occitanie."
    }
  ];

  const handleCallClick = () => {
    window.location.href = 'tel:[TEL]';
  };

  return (
    <Layout>
      {/* Header Section */}
      <section className="hero section-sm">
        <div className="hero-content">
          <div className="container text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] mb-6">
              Notre méthode de travail
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Découvrez notre approche structurée et professionnelle pour garantir 
              la qualité et votre satisfaction à chaque étape de votre projet.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-['Outfit'] mb-4">
              Une approche méthodique
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Chaque projet suit notre processus éprouvé en 5 étapes pour garantir 
              un résultat conforme à vos attentes et aux normes professionnelles.
            </p>
          </div>

          {/* Desktop Timeline */}
          <div className="hidden lg:block mb-16">
            <div className="relative">
              <div className="absolute top-20 left-0 right-0 h-0.5 bg-blue-200"></div>
              <div className="flex justify-between">
                {processSteps.map((step, index) => (
                  <div key={step.step} className="flex-1 relative">
                    <div className="flex flex-col items-center text-center max-w-xs mx-auto">
                      <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6 relative z-10">
                        <span className="text-2xl font-bold text-white">{step.step}</span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 font-['Outfit'] mb-3">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-6 mb-16">
            {processSteps.map((step) => (
              <Card key={step.step} className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-white">{step.step}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 font-['Outfit'] mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">
                      {step.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-['Outfit'] mb-4">
              Nos valeurs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ce qui nous guide au quotidien pour vous offrir le meilleur service
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="card-hover p-8">
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 font-['Outfit'] mb-3">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Company */}
      <section className="section">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <Card className="p-12">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 font-['Outfit'] mb-6">
                    À propos de [MARQUE]
                  </h2>
                  <div className="space-y-4 text-gray-600 leading-relaxed">
                    <p>
                      Entreprise de couverture basée à <strong>Cugnaux</strong> depuis plus de 15 ans, 
                      [MARQUE] accompagne les particuliers et professionnels dans tous leurs 
                      projets de toiture sur Toulouse et l'Occitanie.
                    </p>
                    <p>
                      Nous travaillons dans le <strong>respect des DTU</strong> (Documents Techniques Unifiés) 
                      et bénéficions d'une <strong>assurance décennale et RC professionnelle</strong> 
                      pour garantir vos travaux.
                    </p>
                    <p>
                      Nos chantiers sont pilotés par un <strong>interlocuteur unique</strong> qui vous 
                      accompagne du devis à la réception, assurant un suivi personnalisé 
                      et une communication transparente.
                    </p>
                  </div>
                  
                  <div className="mt-8 space-y-4">
                    <div className="flex flex-wrap gap-3">
                      <Badge className="bg-green-100 text-green-800 px-3 py-1">
                        <Award className="w-4 h-4 mr-2" />
                        Décennale & RC Pro
                      </Badge>
                      <Badge className="bg-blue-100 text-blue-800 px-3 py-1">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Qualibat RGE
                      </Badge>
                      <Badge className="bg-purple-100 text-purple-800 px-3 py-1">
                        <Users className="w-4 h-4 mr-2" />
                        +15 ans d'expérience
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="stats-grid">
                    <div className="stat-item">
                      <div className="stat-number text-2xl">+500</div>
                      <div className="stat-label">chantiers réalisés</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number text-2xl">4,8/5</div>
                      <div className="stat-label">satisfaction client</div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-4">Zone d'intervention</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>• Toulouse</div>
                      <div>• Cugnaux</div>
                      <div>• Colomiers</div>
                      <div>• Tournefeuille</div>
                      <div>• Blagnac</div>
                      <div>• Balma</div>
                      <div>• Muret</div>
                      <div className="col-span-2 text-blue-600 font-semibold">+ Toute l'Occitanie</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-blue-600 text-white">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-4">
            Confiez-nous votre projet
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Bénéficiez de notre expertise et de notre méthode éprouvée pour vos travaux de toiture
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn bg-white text-blue-600 hover:bg-gray-100">
              Demander un devis gratuit
              <ArrowRight className="w-5 h-5" />
            </Link>
            <button 
              onClick={handleCallClick}
              className="btn btn-ghost text-white border-white hover:bg-white/10"
            >
              <Phone className="w-5 h-5 mr-2" />
              Nous appeler
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;